import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Eye, Pencil, Plus, Zap } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../../api';
import { pode as podeFazer, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { tiposDe } from '../../componentes/formatos';
import {
  NOME_EXIGE_FOTO, NOME_NATUREZA, NOME_PENDENCIA, NOME_TIPO_RESPOSTA, resumoRegra,
  type Bloco, type ItemBiblioteca,
} from './comum';

interface TipoOs { id: string; nome: string; natureza: string }
interface Categoria { id: string; codigo: string; nome: string; pai_id: string | null; pai_nome: string | null }

/**
 * Biblioteca de checklist. Um bloco vale para uma natureza (todas as preventivas) ou para um tipo de OS.
 * Bloco com categoria repete para cada equipamento daquela categoria no local — por isso não existe
 * "checklist do condomínio X": o que muda de um cliente para outro é o parque instalado e, quando preciso, um ajuste.
 */
export function Checklists() {
  const { usuario } = useSessao();
  const pode = podeFazer(usuario, 'checklist.editar');
  const [blocos, setBlocos] = useState<Bloco[] | null>(null);
  const [tipos, setTipos] = useState<TipoOs[]>([]);
  const [cats, setCats] = useState<Categoria[]>([]);
  const [filtro, setFiltro] = useState('');
  const [inativos, setInativos] = useState(false);
  const [edicao, setEdicao] = useState<{ tipo: 'bloco'; bloco?: Bloco } | { tipo: 'item'; bloco: Bloco; item?: ItemBiblioteca } | { tipo: 'previa' } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(() => { api<Bloco[]>('/checklists').then(setBlocos).catch((e) => setErro(e.message)); }, []);
  useEffect(() => {
    carregar();
    api<TipoOs[]>('/tipos-os').then(setTipos).catch(() => {});
    api<Categoria[]>('/categorias-ativo').then(setCats).catch(() => {});
  }, [carregar]);
  const salvo = () => { setEdicao(null); carregar(); };

  const visiveis = (blocos ?? []).filter((b) => (inativos || b.ativo) && (!filtro || b.natureza_efetiva === filtro));
  const naturezas = [...new Set((blocos ?? []).map((b) => b.natureza_efetiva))];

  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Configuração</span>
          <h1 className="titulo">Checklists</h1>
        </div>
        <div className="acoes-cabecalho">
          <Link className="btn-fantasma" to="/checklists/tarefas"><Zap size={16} aria-hidden /> Tarefas rápidas</Link>
          <button className="btn-fantasma" onClick={() => setEdicao({ tipo: 'previa' })}><Eye size={16} aria-hidden /> Ver como fica numa OS</button>
          {pode && <button className="btn" onClick={() => setEdicao({ tipo: 'bloco' })}><Plus size={18} aria-hidden /> Bloco</button>}
        </div>
      </div>
      <p className="nota bloco-explica">
        O checklist da OS é montado com os blocos do tipo de OS. Bloco <strong>por equipamento</strong> repete para cada equipamento daquela
        categoria cadastrado no parque. Diferenças de um cliente se resolvem com <strong>ajustes</strong> na página do cliente, sem copiar o checklist.
        Mudança aqui vale para OS que ainda não começaram; depois do check-in o checklist da OS fica congelado.
      </p>

      <div className="barra-filtros">
        <div className="segmentos">
          <button className={filtro === '' ? 'ativo' : ''} onClick={() => setFiltro('')}>Todos</button>
          {naturezas.map((n) => <button key={n} className={filtro === n ? 'ativo' : ''} onClick={() => setFiltro(n)}>{NOME_NATUREZA[n] ?? n}</button>)}
        </div>
        <label className="check"><input type="checkbox" checked={inativos} onChange={(e) => setInativos(e.target.checked)} /> Mostrar fora de uso</label>
      </div>

      {erro && <p className="alerta" role="alert">{erro}</p>}
      {!blocos ? <p className="nota">Carregando…</p> : visiveis.length === 0 ? (
        <div className="cartao vazio"><p>Nenhum bloco ainda. {pode ? 'Comece pelo que todo atendimento precisa (ex.: "Fechamento da corretiva") e depois os blocos por equipamento.' : 'Peça ao administrador para montar a biblioteca.'}</p></div>
      ) : visiveis.map((b) => (
        <section key={b.id} className={`cartao bloco-checklist${b.ativo ? '' : ' inativo'}`}>
          <header className="bloco-topo">
            <div>
              <h2>{b.nome} {!b.ativo && <span className="pill-breve"><i aria-hidden /> fora de uso</span>}</h2>
              <p className="nota">
                {b.tipo_os ? `Tipo de OS: ${b.tipo_os}` : `Toda OS ${NOME_NATUREZA[b.natureza ?? ''] ?? b.natureza}`}
                {' · '}{b.categoria ? `por equipamento (${b.categoria}) · ${b.escopo_ativo === 'todos_do_local' ? 'todos do parque' : 'só os que o técnico escolher'}` : 'uma vez por OS'}
                {b.ajustes > 0 && ` · ${b.ajustes} ajuste${b.ajustes > 1 ? 's' : ''} de cliente`}
                {b.usado_em_os > 0 && ` · usado em ${b.usado_em_os} OS`}
              </p>
              {b.descricao && <p className="texto-longo">{b.descricao}</p>}
            </div>
            {pode && <div className="acoes-cabecalho">
              <button className="btn-fantasma" onClick={() => setEdicao({ tipo: 'item', bloco: b })}><Plus size={16} aria-hidden /> Item</button>
              <button className="btn-icone" onClick={() => setEdicao({ tipo: 'bloco', bloco: b })} aria-label={`Editar ${b.nome}`}><Pencil size={16} /></button>
            </div>}
          </header>
          {b.itens.length === 0 ? <p className="nota">Sem itens.</p> : (
            <table className="tabela tabela-itens">
              <thead><tr><th>#</th><th>Item</th><th>Resposta</th><th>Obrigatório</th>{pode && <th aria-label="Ações" />}</tr></thead>
              <tbody>
                {b.itens.filter((i) => inativos || i.ativo).map((i) => (
                  <tr key={i.id} className={i.ativo ? '' : 'inativo'}>
                    <td className="num">{i.ordem}</td>
                    <td><strong>{i.texto}</strong>{i.ajuda && <small>{i.ajuda}</small>}<small className="mono">{i.codigo}</small></td>
                    <td><small>{resumoRegra(i)}</small></td>
                    <td>{i.bloqueante ? <span className="pill-seguranca" title="NOK impede fechar como resolvida e gera aviso ao síndico">segurança</span> : i.obrigatorio ? 'Sim' : 'Não'}</td>
                    {pode && <td className="acoes"><button className="btn-icone" onClick={() => setEdicao({ tipo: 'item', bloco: b, item: i })} aria-label={`Editar ${i.texto}`}><Pencil size={16} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}

      <Modal titulo={edicao?.tipo === 'bloco' && edicao.bloco ? 'Editar bloco' : 'Novo bloco'} aberto={edicao?.tipo === 'bloco'} aoFechar={() => setEdicao(null)}>
        {edicao?.tipo === 'bloco' && <FormBloco inicial={edicao.bloco} tipos={tipos} cats={cats} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />}
      </Modal>
      <Modal titulo={edicao?.tipo === 'item' && edicao.item ? 'Editar item' : 'Novo item'} aberto={edicao?.tipo === 'item'} aoFechar={() => setEdicao(null)}>
        {edicao?.tipo === 'item' && <FormItem blocoId={edicao.bloco.id} inicial={edicao.item} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />}
      </Modal>
      <Modal titulo="Como fica numa OS" aberto={edicao?.tipo === 'previa'} aoFechar={() => setEdicao(null)} classe="modal-largo">
        {edicao?.tipo === 'previa' && <Previa tipos={tipos} />}
      </Modal>
    </>
  );
}

function FormBloco({ inicial, tipos, cats, aoSalvar, aoCancelar }: { inicial?: Bloco; tipos: TipoOs[]; cats: Categoria[]; aoSalvar: () => void; aoCancelar: () => void }) {
  const [f, setF] = useState({
    nome: inicial?.nome ?? '', descricao: inicial?.descricao ?? '', escopo: inicial?.tipo_os_id ? 'tipo' : 'natureza',
    natureza: inicial?.natureza ?? 'preventiva', tipo_os_id: inicial?.tipo_os_id ?? '', categoria_id: inicial?.categoria_id ?? '',
    escopo_ativo: inicial?.escopo_ativo ?? 'todos_do_local', ordem: String(inicial?.ordem ?? 100), ativo: inicial?.ativo ?? true,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    const corpo = {
      nome: f.nome, descricao: f.descricao || null,
      natureza: f.escopo === 'natureza' ? f.natureza : null, tipo_os_id: f.escopo === 'tipo' ? f.tipo_os_id || null : null,
      categoria_id: f.categoria_id || null, escopo_ativo: f.escopo_ativo, ordem: Number(f.ordem) || 100,
    };
    try {
      if (inicial) await api(`/checklists/${inicial.id}`, { metodo: 'PATCH', corpo: { ...corpo, ativo: f.ativo, versao: inicial.versao } });
      else await api('/checklists', { metodo: 'POST', corpo });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <label>Nome<input required minLength={3} maxLength={120} value={f.nome} onChange={set('nome')} placeholder="Ex.: Preventiva de motor de portão" autoFocus /></label>
      <div className="linha-2">
        <label>Vale para
          <select value={f.escopo} onChange={set('escopo')}>
            <option value="natureza">Toda OS de uma natureza</option>
            <option value="tipo">Um tipo de OS específico</option>
          </select>
        </label>
        {f.escopo === 'natureza' ? (
          <label>Natureza<select value={f.natureza} onChange={set('natureza')}>{tiposDe(NOME_NATUREZA).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>
        ) : (
          <label>Tipo de OS<select required value={f.tipo_os_id} onChange={set('tipo_os_id')}><option value="">Escolha</option>{tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}</select></label>
        )}
      </div>
      <div className="linha-2">
        <label>Repete por equipamento?
          <select value={f.categoria_id} onChange={set('categoria_id')}>
            <option value="">Não — uma vez por OS</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.pai_nome ? `${c.pai_nome} › ${c.nome}` : `${c.nome} (todas)`}</option>)}
          </select>
        </label>
        {f.categoria_id && (
          <label>Quais equipamentos
            <select value={f.escopo_ativo} onChange={set('escopo_ativo')}>
              <option value="todos_do_local">Todos do parque (preventiva)</option>
              <option value="escolhidos">Só os que o técnico escolher (corretiva)</option>
            </select>
          </label>
        )}
      </div>
      <div className="linha-2">
        <label>Ordem na OS<input type="number" min={0} max={100000} value={f.ordem} onChange={set('ordem')} /></label>
        {inicial && <label className="check"><input type="checkbox" checked={f.ativo} onChange={(e) => setF((x) => ({ ...x, ativo: e.target.checked }))} /> Em uso</label>}
      </div>
      <label>Descrição (opcional)<textarea rows={2} maxLength={1000} value={f.descricao} onChange={set('descricao')} /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || f.nome.trim().length < 3}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  );
}

const lista = (t: string) => t.split(',').map((x) => x.trim()).filter(Boolean);

/** Formulário de item — usado na biblioteca e no ajuste "acrescentar" do cliente. */
export function CamposItem({ f, setF }: { f: CampoItem; setF: (fn: (x: CampoItem) => CampoItem) => void }) {
  const set = (k: keyof CampoItem) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <>
      <label>Texto do item<input required minLength={3} maxLength={300} value={f.texto} onChange={set('texto')} placeholder="Ex.: Tensão de alimentação do motor" /></label>
      <label>Orientação ao técnico (opcional)<input maxLength={500} value={f.ajuda} onChange={set('ajuda')} placeholder="Ex.: medir com o portão parado" /></label>
      <div className="linha-2">
        <label>Tipo de resposta<select value={f.tipo_resposta} onChange={set('tipo_resposta')}>{tiposDe(NOME_TIPO_RESPOSTA).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>
        <label>Obrigatório<select value={f.obrigatorio ? 's' : 'n'} disabled={f.bloqueante} onChange={(e) => setF((x) => ({ ...x, obrigatorio: e.target.value === 's' }))}><option value="s">Sim</option><option value="n">Não</option></select></label>
      </div>
      {!['texto', 'foto'].includes(f.tipo_resposta) && (
        <label className="check"><input type="checkbox" checked={f.bloqueante} onChange={(e) => setF((x) => ({ ...x, bloqueante: e.target.checked, obrigatorio: e.target.checked || x.obrigatorio }))} />
          Item de segurança (bloqueante)
          <small className="ajuda">Ex.: antiesmagamento, intertravamento da eclusa. Se der NOK, a OS não fecha como resolvida, gera pendência para comunicar o síndico e avisa o escritório. O cliente não pode dispensar.</small></label>
      )}
      {f.tipo_resposta === 'numero' && (
        <div className="linha-3">
          <label>Mínimo aceito<input type="number" step="any" value={f.minimo} onChange={set('minimo')} /></label>
          <label>Máximo aceito<input type="number" step="any" value={f.maximo} onChange={set('maximo')} /></label>
          <label>Unidade<input maxLength={15} value={f.unidade} onChange={set('unidade')} placeholder="V, A, dBm" /></label>
        </div>
      )}
      {f.tipo_resposta === 'opcao' && <label>Opções (separe por vírgula)<input value={f.opcoes} onChange={set('opcoes')} placeholder="bom, regular, ruim" /></label>}
      {(f.tipo_resposta === 'opcao' || f.tipo_resposta === 'sim_nao') && (
        <label>Contam como problema (separe por vírgula)<input value={f.nok_valores} onChange={set('nok_valores')} placeholder={f.tipo_resposta === 'sim_nao' ? 'nao' : 'ruim'} />
          <small className="ajuda">{f.tipo_resposta === 'sim_nao' ? 'Use "sim" ou "nao".' : 'Precisam estar entre as opções.'}</small></label>
      )}
      <div className="linha-2">
        {f.tipo_resposta !== 'foto' && <label>Foto<select value={f.exige_foto} onChange={set('exige_foto')}>{tiposDe(NOME_EXIGE_FOTO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>}
        <label>Se der problema (NOK)
          <select value={f.pendencia_tipo} onChange={set('pendencia_tipo')}>
            <option value="">Não gera pendência</option>
            {tiposDe(NOME_PENDENCIA).map(([v, r]) => <option key={v} value={v}>Gera pendência: {r.toLowerCase()}</option>)}
          </select>
        </label>
      </div>
    </>
  );
}

export interface CampoItem { texto: string; ajuda: string; tipo_resposta: string; obrigatorio: boolean; minimo: string; maximo: string; unidade: string; opcoes: string; nok_valores: string; exige_foto: string; pendencia_tipo: string; ordem: string; bloqueante: boolean }
export const ITEM_VAZIO: CampoItem = { texto: '', ajuda: '', tipo_resposta: 'ok_nok', obrigatorio: true, minimo: '', maximo: '', unidade: '', opcoes: '', nok_valores: '', exige_foto: 'nunca', pendencia_tipo: '', ordem: '', bloqueante: false };

/** Campos do formulário → corpo da API (só manda o que faz sentido para o tipo de resposta). */
export function corpoItem(f: CampoItem) {
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  return {
    texto: f.texto.trim(), ajuda: f.ajuda.trim() || null, tipo_resposta: f.tipo_resposta, obrigatorio: f.obrigatorio,
    minimo: f.tipo_resposta === 'numero' ? num(f.minimo) : null, maximo: f.tipo_resposta === 'numero' ? num(f.maximo) : null,
    unidade: f.tipo_resposta === 'numero' ? f.unidade.trim() || null : null,
    opcoes: f.tipo_resposta === 'opcao' ? lista(f.opcoes) : null,
    nok_valores: ['opcao', 'sim_nao'].includes(f.tipo_resposta) && lista(f.nok_valores).length ? lista(f.nok_valores) : null,
    exige_foto: f.tipo_resposta === 'foto' ? 'nunca' : f.exige_foto, pendencia_tipo: f.pendencia_tipo || null,
    bloqueante: f.bloqueante && !['texto', 'foto'].includes(f.tipo_resposta),
    ...(f.ordem.trim() ? { ordem: Number(f.ordem) } : {}),
  };
}

function FormItem({ blocoId, inicial, aoSalvar, aoCancelar }: { blocoId: string; inicial?: ItemBiblioteca; aoSalvar: () => void; aoCancelar: () => void }) {
  const [f, setF] = useState<CampoItem>(inicial ? {
    texto: inicial.texto, ajuda: inicial.ajuda ?? '', tipo_resposta: inicial.tipo_resposta, obrigatorio: inicial.obrigatorio,
    minimo: inicial.minimo ?? '', maximo: inicial.maximo ?? '', unidade: inicial.unidade ?? '', opcoes: (inicial.opcoes ?? []).join(', '),
    nok_valores: (inicial.nok_valores ?? []).join(', '), exige_foto: inicial.exige_foto, pendencia_tipo: inicial.pendencia_tipo ?? '', ordem: String(inicial.ordem), bloqueante: inicial.bloqueante,
  } : ITEM_VAZIO);
  const [ativo, setAtivo] = useState(inicial?.ativo ?? true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      if (inicial) await api(`/checklists/itens/${inicial.id}`, { metodo: 'PATCH', corpo: { ...corpoItem(f), ativo, versao: inicial.versao } });
      else await api(`/checklists/${blocoId}/itens`, { metodo: 'POST', corpo: corpoItem(f) });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <CamposItem f={f} setF={setF} />
      <div className="linha-2">
        <label>Ordem<input type="number" min={0} value={f.ordem} onChange={(e) => setF((x) => ({ ...x, ordem: e.target.value }))} placeholder="no fim" /></label>
        {inicial && <label className="check"><input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Em uso</label>}
      </div>
      {inicial && <p className="nota">Código <span className="mono">{inicial.codigo}</span> não muda (indicadores e ajustes de cliente apontam para ele). OS já iniciadas guardam a versão antiga.</p>}
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || f.texto.trim().length < 3}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  );
}

interface BlocoPrevia { bloco_id: string; nome: string; categoria: string | null; escopo_ativo: string; repeticoes: number; ativos_elegiveis: { id: string; apelido: string }[];
  itens: { codigo: string; texto: string; tipo_resposta: string; obrigatorio: boolean; ajuste_id: string | null; regra: Record<string, never> }[] }

/** Prévia: tipo de OS + local → o checklist que o técnico vai ver (com ajustes aplicados). */
export function Previa({ tipos, localInicial }: { tipos: TipoOs[]; localInicial?: { clienteId: string; clienteNome: string } }) {
  const [tipo, setTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<{ id: string; razao_social: string; nome_fantasia: string | null }[]>([]);
  const [cliente, setCliente] = useState(localInicial ? { id: localInicial.clienteId, nome: localInicial.clienteNome } : null);
  const [locais, setLocais] = useState<{ id: string; nome: string; ativo: boolean }[]>([]);
  const [local, setLocal] = useState('');
  const [previa, setPrevia] = useState<BlocoPrevia[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!busca.trim() || cliente) return setAchados([]);
    const t = setTimeout(() => api<{ itens: typeof achados }>(`/clientes?busca=${encodeURIComponent(busca)}`).then((r) => setAchados(r.itens.slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [busca, cliente]);
  useEffect(() => {
    if (!cliente) return setLocais([]);
    api<{ locais: typeof locais }>(`/clientes/${cliente.id}`).then((c) => { const a = c.locais.filter((l) => l.ativo); setLocais(a); if (a.length === 1) setLocal(a[0]!.id); }).catch(() => {});
  }, [cliente]);
  useEffect(() => {
    setPrevia(null); setErro(null);
    if (!tipo || !local) return;
    api<BlocoPrevia[]>(`/checklists/previa?tipo_os_id=${tipo}&local_id=${local}`).then(setPrevia).catch((e) => setErro(e.message));
  }, [tipo, local]);

  return (
    <div className="form">
      <div className="linha-3">
        <label>Tipo de OS<select value={tipo} onChange={(e) => setTipo(e.target.value)}><option value="">Escolha</option>{tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}</select></label>
        <div className="campo"><span className="rotulo-campo">Cliente</span>
          {cliente ? <div className="campo-mascarado"><span>{cliente.nome}</span>{!localInicial && <button type="button" className="btn-link" onClick={() => { setCliente(null); setLocal(''); }}>trocar</button>}</div> : (
            <div className="autocompletar">
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome do cliente" />
              {achados.length > 0 && <ul>{achados.map((c) => <li key={c.id}><button type="button" onClick={() => { setCliente({ id: c.id, nome: c.nome_fantasia ?? c.razao_social }); setBusca(''); }}><strong>{c.nome_fantasia ?? c.razao_social}</strong></button></li>)}</ul>}
            </div>
          )}
        </div>
        <label>Local<select value={local} onChange={(e) => setLocal(e.target.value)} disabled={!cliente}><option value="">Escolha</option>{locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}</select></label>
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {previa && (previa.length === 0 ? <p className="nota">Nenhum bloco de checklist para este tipo de OS. A OS sai sem checklist.</p> : previa.map((b) => (
        <div key={b.bloco_id} className="previa-bloco">
          <h3>{b.nome}</h3>
          <p className="nota">
            {!b.categoria ? 'Uma vez por OS' : b.escopo_ativo === 'todos_do_local'
              ? (b.repeticoes ? `Repete para ${b.repeticoes} equipamento${b.repeticoes > 1 ? 's' : ''}: ${b.ativos_elegiveis.map((a) => a.apelido).join(', ')}` : `Nenhum equipamento de ${b.categoria} cadastrado neste local — o bloco não aparece. Cadastre o parque instalado.`)
              : `Técnico escolhe entre ${b.ativos_elegiveis.length} equipamento(s) de ${b.categoria}`}
          </p>
          <ul className="lista-simples">
            {b.itens.map((i) => <li key={i.codigo}>{i.texto} <small>{[NOME_TIPO_RESPOSTA[i.tipo_resposta], i.obrigatorio ? null : 'opcional', i.ajuste_id ? 'ajuste deste cliente' : null].filter(Boolean).join(' · ')}</small></li>)}
          </ul>
        </div>
      )))}
    </div>
  );
}
